import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import Layout from '../components/layout/Layout';
import { useSiteContent } from '../context/SiteContentContext';
import {
  Phone,
  Zap,
  Award,
  Headphones,
  CheckCircle2,
  Clock,
  Shield,
  Users,
  Target,
  TrendingUp
} from 'lucide-react';

const iconMap = {
  Zap: Zap,
  Award: Award,
  HeadphonesIcon: Headphones
};

const DiferenciaisPage = () => {
  const { siteContent } = useSiteContent();
  const companyInfo = siteContent.companyInfo;
  const differentials = siteContent.differentials;
  const images = siteContent.images;
  const handleWhatsApp = () => {
    window.open(
      `https://wa.me/${companyInfo.whatsapp}?text=${encodeURIComponent(companyInfo.whatsappMessage)}`,
      '_blank'
    );
  };

  const stats = [
    { number: '500+', label: 'Clientes Atendidos' },
    { number: '10+', label: 'Anos de Experiência' },
    { number: '99.9%', label: 'Uptime Garantido' },
    { number: '15min', label: 'Tempo Médio de Resposta' }
  ];

  const benefits = [
    {
      icon: Clock,
      title: 'Disponibilidade 24/7',
      description: 'Suporte disponível a qualquer hora para atender chamados críticos.'
    },
    {
      icon: Shield,
      title: 'Segurança Garantida',
      description: 'Protocolos rigorosos de segurança para proteger seus dados.'
    },
    {
      icon: Users,
      title: 'Equipe Certificada',
      description: 'Profissionais com certificações Microsoft, AWS, Cisco e mais.'
    },
    {
      icon: Target,
      title: 'Foco em Resultados',
      description: 'Soluções orientadas a resultados mensuraráveis para seu negócio.'
    },
    {
      icon: TrendingUp,
      title: 'Melhoria Contínua',
      description: 'Análise constante para otimizar processos e reduzir custos.'
    },
    {
      icon: Zap,
      title: 'Agilidade no Atendimento',
      description: 'Processos estruturados para resolução rápida de problemas.'
    }
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" />
        <div className="absolute top-1/3 right-10 w-96 h-96 bg-orange-500/20 rounded-full blur-3xl" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-block px-4 py-2 rounded-full bg-white/10 text-white/80 text-sm font-medium mb-6">
              Diferenciais
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Por que escolher a <span className="text-orange-500">DRSUPORTI</span>
            </h1>
            <p className="text-xl text-slate-300 leading-relaxed">
              Descubra o que nos torna a escolha certa para cuidar da tecnologia da sua empresa.
            </p>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <p className="text-4xl md:text-5xl font-bold text-orange-500 mb-2">
                  {stat.number}
                </p>
                <p className="text-slate-600 font-medium">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Main Differentials */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 rounded-full bg-orange-100 text-orange-600 text-sm font-medium mb-4">
              Nossos Pilares
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4">
              O que nos diferencia
            </h2>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {differentials.map((diff, index) => {
              const IconComponent = iconMap[diff.icon];
              return (
                <Card
                  key={diff.id}
                  className="border-0 shadow-xl shadow-slate-200/50 hover:shadow-2xl hover:shadow-orange-100 transition-all duration-500 hover:-translate-y-2 overflow-hidden"
                >
                  <CardContent className="p-0">
                    <div className="h-2 bg-gradient-to-r from-orange-500 to-orange-600" />
                    <div className="p-8">
                      <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center mb-6">
                        {IconComponent && <IconComponent className="w-8 h-8 text-white" />}
                      </div>
                      <h3 className="text-2xl font-bold text-slate-800 mb-4">
                        {diff.title}
                      </h3>
                      <p className="text-slate-600 leading-relaxed text-lg">
                        {diff.description}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Grid */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="inline-block px-4 py-2 rounded-full bg-blue-100 text-blue-600 text-sm font-medium mb-4">
                Benefícios
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-6">
                Vantagens de ter a DRSUPORTI como parceira
              </h2>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                Ao escolher a DRSUPORTI, sua empresa ganha um parceiro comprometido 
                com o sucesso do seu negócio. Oferecemos muito mais do que suporte 
                técnico - entregamos soluções estratégicas.
              </p>

              <div className="space-y-4">
                {[
                  'Redução de custos operacionais com TI',
                  'Maior produtividade da equipe',
                  'Segurança de dados garantida',
                  'Suporte ágil quando você precisa',
                  'Tecnologia sempre atualizada'
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <CheckCircle2 className="w-6 h-6 text-orange-500 flex-shrink-0" />
                    <span className="text-slate-700 font-medium">{item}</span>
                  </div>
                ))}
              </div>

              <Button
                onClick={handleWhatsApp}
                className="mt-8 bg-orange-500 hover:bg-orange-600 text-white font-semibold px-8 py-6 text-lg shadow-lg shadow-orange-500/25"
              >
                <Phone className="w-5 h-5 mr-2" />
                Quero ser parceiro
              </Button>
            </div>

            <div className="grid sm:grid-cols-2 gap-6">
              {benefits.map((benefit, index) => (
                <div
                  key={index}
                  className="p-6 rounded-xl bg-slate-50 hover:bg-orange-50 transition-colors duration-300"
                >
                  <div className="w-12 h-12 rounded-xl bg-white shadow-md flex items-center justify-center mb-4">
                    <benefit.icon className="w-6 h-6 text-orange-500" />
                  </div>
                  <h3 className="font-bold text-slate-800 mb-2">{benefit.title}</h3>
                  <p className="text-sm text-slate-600">{benefit.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-slate-900 to-slate-800 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-1/2 h-full opacity-10">
          <img src={images.services} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-900/95 to-transparent" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Pronto para elevar o nível da sua TI?
            </h2>
            <p className="text-xl text-slate-300 mb-8">
              Fale com nossa equipe e descubra como podemos transformar a tecnologia 
              da sua empresa em uma vantagem competitiva.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={handleWhatsApp}
                size="lg"
                className="bg-orange-500 hover:bg-orange-600 text-white font-semibold px-8 py-6 text-lg shadow-xl shadow-orange-500/25"
              >
                <Phone className="w-5 h-5 mr-2" />
                Fale com um especialista
              </Button>
              <Button
                asChild
                variant="outline"
                size="lg"
                className="border-white/30 text-white hover:bg-white/10 px-8 py-6 text-lg"
              >
                <Link to="/servicos">Conhecer serviços</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default DiferenciaisPage;
